import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Brain, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { apiGet, apiPost } from "@/lib/api";
import { toast } from "sonner";

const VERDICT_CLASS = {
  OVERWEIGHT: "border-success/40 text-success",
  NEUTRAL: "border-border text-muted-foreground",
  UNDERWEIGHT: "border-destructive/40 text-destructive",
};

/**
 * SectorAnalysisCard — surfaces Layer 6 sector-analysis status on Admin
 * Report Monitor. Shows last run summary, cost, per-sector verdict + score,
 * and a manual refresh trigger.
 */
const SectorAnalysisCard = () => {
  const [items, setItems] = useState([]);
  const [lastRun, setLastRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const resp = await apiGet("/sectors/scores");
      setItems(resp?.items || []);
      setLastRun(resp?.last_run || null);
    } catch (e) {
      console.warn(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const refresh = async () => {
    if (busy) return;
    if (
      !window.confirm(
        "Run Layer 6 sector analysis NOW? This calls Claude ~20 times (~$0.25-1.00) and takes 60-120 seconds. Continue?"
      )
    ) return;
    setBusy(true);
    try {
      await apiPost("/admin/sector-analysis/refresh", {});
      toast.success("Sector analysis started", {
        description: "Refresh this page in 60-90 seconds to see the latest verdicts.",
      });
    } catch (e) {
      toast.error("Failed to start sector analysis", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBusy(false);
    }
  };

  const counts = items.reduce(
    (acc, r) => {
      const v = (r.overall_verdict || "NEUTRAL").toUpperCase();
      acc[v] = (acc[v] || 0) + 1;
      acc.stale += r.is_stale ? 1 : 0;
      return acc;
    },
    { OVERWEIGHT: 0, NEUTRAL: 0, UNDERWEIGHT: 0, stale: 0 }
  );

  return (
    <Card className="border bg-card" data-testid="sector-analysis-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-display text-base flex items-center gap-2">
              <Brain className="h-4 w-4 text-primary" />
              Layer 6 — Sector Analysis
            </CardTitle>
            <CardDescription className="mt-1">
              Runs every Sunday 21:00 SLT. Claude analyses each CSE sector on cycle, valuation,
              macro and foreign sentiment. Each stock's COMB score picks up a 0-10 sector
              contribution.
            </CardDescription>
          </div>
          <Button size="sm" onClick={refresh} disabled={busy} data-testid="sector-analysis-refresh-btn">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} />
            Run now
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <Stat
            label="Sectors analysed"
            value={items.length}
            tone="default"
            testId="sector-analysis-stat-count"
          />
          <Stat
            label="OVERWEIGHT"
            value={counts.OVERWEIGHT}
            tone="success"
            testId="sector-analysis-stat-over"
          />
          <Stat
            label="UNDERWEIGHT"
            value={counts.UNDERWEIGHT}
            tone="danger"
            testId="sector-analysis-stat-under"
          />
          <Stat
            label="Last cost"
            value={
              lastRun?.total_cost_usd != null
                ? `$${Number(lastRun.total_cost_usd).toFixed(3)}`
                : "—"
            }
            sub={
              lastRun?.completed_at
                ? new Date(lastRun.completed_at).toLocaleString()
                : "Pending"
            }
            testId="sector-analysis-stat-cost"
          />
        </div>
        {counts.stale > 0 && (
          <div className="flex items-center gap-2 text-[11.5px] text-warning">
            <AlertTriangle className="h-3.5 w-3.5" />
            {counts.stale} sector{counts.stale === 1 ? "" : "s"} stale (older than 7 days) — run a manual refresh to update.
          </div>
        )}
        {loading ? (
          <div className="text-xs text-muted-foreground italic">Loading sector verdicts…</div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-dashed border-border bg-muted/20 p-3 text-xs text-muted-foreground">
            No sector analysis yet. Click <strong>Run now</strong> to populate or wait until the next
            scheduled run on Sunday 21:00 SLT.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border" data-testid="sector-analysis-table">
            <table className="w-full text-xs">
              <thead className="bg-muted/40">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Sector</th>
                  <th className="text-right px-3 py-2 font-medium text-muted-foreground">Score</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground">Verdict</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden md:table-cell">Cycle</th>
                  <th className="text-left px-3 py-2 font-medium text-muted-foreground hidden md:table-cell">Macro</th>
                  <th className="text-right px-3 py-2 font-medium text-muted-foreground">Cost</th>
                  <th className="text-right px-3 py-2 font-medium text-muted-foreground hidden md:table-cell">Analysed</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const v = (r.overall_verdict || "NEUTRAL").toUpperCase();
                  return (
                    <tr key={r.sector} className="border-t border-border" data-testid={`sector-analysis-row-${r.sector?.replace(/\s+/g, "-")}`}>
                      <td className="px-3 py-1.5 font-medium">{r.sector}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {r.total_score}/{r.max_score || 10}
                      </td>
                      <td className="px-3 py-1.5">
                        <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${VERDICT_CLASS[v] || ""}`}>
                          {v}
                        </Badge>
                        {r.is_stale && (
                          <Badge variant="outline" className="ml-1 text-[10px] px-1 py-0 border-warning/40 text-warning gap-0.5">
                            <AlertTriangle className="h-2.5 w-2.5" /> stale
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground hidden md:table-cell">
                        {r.cycle_position?.replace(/_/g, " ") || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-muted-foreground hidden md:table-cell">
                        {r.macro_assessment || "—"}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">
                        {r.cost_usd != null ? `$${Number(r.cost_usd).toFixed(3)}` : "—"}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground hidden md:table-cell">
                        {r.analysed_at ? new Date(r.analysed_at).toLocaleDateString() : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, sub, tone = "default", testId }) => {
  const toneClass = {
    success: "text-[hsl(var(--success))]",
    danger: "text-[hsl(var(--destructive))]",
    default: "",
  }[tone];
  return (
    <div className="rounded-md border border-border bg-background p-2.5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-sm font-semibold tabular-nums mt-0.5 ${toneClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{sub}</div>}
    </div>
  );
};

export default SectorAnalysisCard;
